import { Controller } from "@hotwired/stimulus";
import Plyr from "plyr";

/**
 * public--audio-interview-question controller
 * Handles recording, timer, and simple bar visualization for the audio interview card.
 *
 * Usage (example in ERB):
 * <div data-controller="public--audio-interview-question"
 *      data-public--audio-interview-question-time-limit-value="300"> ... </div>
 */
export default class extends Controller {
  static targets = [
    "recordButton",
    "recordIcon",
    "statusText",
    "timer",
    "audioPlayer",
    "warningNotice",
    "retryButton",
    "nextButton",
    "visualization",
    "visualizationRail",
    "deviceContainer",
    "deviceSelect"
  ];

  static values = {
    appId: String, // public_uid of the App
    timeLimit: Number, // seconds (optional, defaults to 300 if not provided)
    deviceId: String,
    silenceGrace: Number, // seconds before showing "no sound" warning (default 3s)
    updateUrl: String, // server PATCH endpoint to save audio
    answerId: Number // AudioInterviewAnswer ID
  };

  connect() {
    this._mediaRecorder = null;
    this._audioChunks = [];
    this._isRecording = false;
    this._startTime = null;
    this._timerId = null;
    this._animationId = null;
    this._audioContext = null;
    this._analyser = null;
    this._micSource = null;
    this._dataArray = null;
    this._stream = null;
    this._players = [];

    this.ensureBars();
    this.resetUI();
    this._silentFrames = 0;
    this._silenceStartTs = null;
    this._warnedSilence = false;
    this._silenceGraceMs = (this.hasSilenceGraceValue ? Number(this.silenceGraceValue) : 3) * 1000;

    // Enhance any <audio class="audio-player">
    this.element.querySelectorAll("audio.audio-player").forEach((el) => {
      this._players.push(new Plyr(el, {
        controls: ['play', 'progress', 'current-time', 'mute', 'volume']
      }));
    });

    // Restore previously chosen device if present
    try {
      const savedId = localStorage.getItem("aiMicDeviceId");
      if (savedId) this.deviceIdValue = savedId;
    } catch (_) {}

    // Populate device list on load (labels appear after a prior permission grant)
    try { this.populateDeviceList(); } catch (_) {}

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.showPermission(
        "Your browser does not support audio recording. Please use a modern browser like Chrome, Firefox, or Safari."
      );
      this.recordButtonTarget.disabled = true;
    }
  }

  disconnect() {
    this.cleanup();
  }

  // Actions
  toggle() {
    if (this._isRecording) {
      this.stop();
    } else {
      this.start();
    }
  }

  reset() {
    this.cleanup(); // stop anything active
    this._audioChunks = [];
    this.resetUI();
  }

  async next() {
    try {
      if (!this._latestAudioBlob || !this.hasUpdateUrlValue || !this.hasAnswerIdValue) {
        // Fall back to event dispatch if upload cannot proceed
        this.element.dispatchEvent(new CustomEvent("audio-interview:next", { bubbles: true }));
        return;
      }

      // Determine filename and extension from MIME type
      var type = this._latestAudioType || (this._latestAudioBlob && this._latestAudioBlob.type) || "audio/webm";
      var ext = "webm";
      if (type.indexOf("mp4") >= 0) ext = "mp4";
      else if (type.indexOf("mpeg") >= 0 || type.indexOf("mp3") >= 0) ext = "mp3";
      var filename = "audio_answer_" + this.answerIdValue + "." + ext;

      var fd = new FormData();
      fd.append("audio_file", this._latestAudioBlob, filename);
      fd.append("answer_id", String(this.answerIdValue));
      var dur = Number.isFinite(this._lastDurationSeconds)
        ? this._lastDurationSeconds
        : Math.max(0, Math.floor((Date.now() - (this._startTime || Date.now())) / 1000));
      fd.append("duration_seconds", String(dur));

      var tokenEl = document.querySelector('meta[name="csrf-token"]');
      var csrf = tokenEl && tokenEl.content;

      const resp = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: Object.assign(
          csrf ? { "X-CSRF-Token": csrf } : {},
          { "X-Requested-With": "XMLHttpRequest" } // <-- important
        ),
        body: fd,
        credentials: "same-origin"
      });

      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          if (data.finished) {
            window.location.href = `/apps/${this.appIdValue}/audio_interview/success`;
          } else {
            window.location.reload(); // refresh to load next question
          }
        } else {
          console.error("Upload failed:", data.error);
        }
      } else {
        throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
      }
    } catch (e) {
      console.warn("[public--audio-interview-question] Upload failed:", e);
      this.element.dispatchEvent(new CustomEvent("audio-interview:next", { bubbles: true }));
    }
  }

  // Core
  async start() {
    try {
      // Request microphone stream
      // Prefer previously selected device if provided
      const preferred = this.hasDeviceIdValue ? this.deviceIdValue : null;
      this._stream = await this.acquireMicrophoneStream(preferred);

      // Debug: log which input device and settings are actually active
      try {
        const aTrack = (this._stream.getAudioTracks() || [])[0];
        const settings = aTrack && aTrack.getSettings ? aTrack.getSettings() : {};
        // Persist the actual device the browser granted permission for, so subsequent starts use it explicitly.
        if (settings && settings.deviceId) {
          this.deviceIdValue = settings.deviceId;
          try { localStorage.setItem("aiMicDeviceId", settings.deviceId); } catch (_) {}
          // If selector exists and is hidden, ensure it preselects the active device next time it's opened.
          try { this.populateDeviceList(); } catch (_) {}
        }
      } catch (_) {}

      this.hidePermission();

      // Visualization setup
      this._audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this._analyser = this._audioContext.createAnalyser();
      this._micSource = this._audioContext.createMediaStreamSource(this._stream);

      // Ensure AudioContext is running; if suspended no audio flows
      try {
        if (this._audioContext.state === "suspended") {
          await this._audioContext.resume();
        }
      } catch (_) {}

      // Keep the original microphone track explicitly enabled
      try {
        (this._stream.getAudioTracks() || []).forEach((t) => (t.enabled = true));
      } catch (_) {}

      // WebAudio graph for visualization only:
      // micSource -> analyser
      this._analyser.fftSize = 64;
      this._dataArray = new Uint8Array(this._analyser.frequencyBinCount);
      this._micSource.connect(this._analyser);

      // MediaRecorder setup (choose best supported type)
      const preferredTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp3",
        ""
      ];
      const chosenType = preferredTypes.find((t) => {
        try {
          return t === "" || (window.MediaRecorder && MediaRecorder.isTypeSupported(t));
        } catch (_) {
          return t === "";
        }
      }) || "";

      // Record directly from microphone stream for maximum compatibility
      const recordStream = this._stream;
      this._mediaRecorder = new MediaRecorder(recordStream, chosenType ? { mimeType: chosenType } : undefined);
      this._audioChunks = [];

      this._mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) this._audioChunks.push(event.data);
      };

      this._mediaRecorder.onstop = () => {
        // Prefer actual chunk type; fall back to recorder type or browser-specific default
        const firstChunk = this._audioChunks[0];
        const chunkType = firstChunk && firstChunk.type;
        const recorderType = (this._mediaRecorder && this._mediaRecorder.mimeType) || "";
        const fallbackType = /Safari|iPhone|iPad|iPod/.test(navigator.userAgent) ? "audio/mp4" : "audio/webm";
        const type = chunkType || recorderType || fallbackType;

        const audioBlob = new Blob(this._audioChunks, { type });
        // Cache latest recording for upload on "Next"
        this._latestAudioBlob = audioBlob;
        this._latestAudioType = type;

        const audioUrl = URL.createObjectURL(audioBlob);
        this.audioPlayerTarget.src = audioUrl;
        this.audioPlayerTarget.muted = false;
        this.audioPlayerTarget.volume = 1.0;
        this.audioPlayerTarget.style.display = "block";
        this.retryButtonTarget.style.setProperty('display', 'inline-block', 'important');
        this.nextButtonTarget.style.setProperty('display', 'inline-block', 'important');

        // Compute and cache duration using the recording session timestamps
        try {
          const stopTs = Date.now();
          const startTs = this._startTime || stopTs;
          this._lastDurationSeconds = Math.max(0, Math.floor((stopTs - startTs) / 1000));
        } catch (_) {
          this._lastDurationSeconds = undefined;
        }

        // Heuristic: flag if blob is suspiciously small (likely silence)
        if (audioBlob.size < 4096) {
          console.warn("[public--audio-interview-question] Very small audio file; input may be muted or device gain is zero.");
          this.warningNoticeTarget.style.display = "block";
          const span = this.warningNoticeTarget.querySelector(".ui-warning-box__message");
          const msg = "We didn't detect audio in your recording. Check your microphone input level or device selection and try again.";
          span.textContent = msg;
        }

        // Stop tracks and close context after stopping
        try {
          if (this._stream) this._stream.getTracks().forEach((t) => t.stop());
        } catch (_) {}
        this._stream = null;

        if (this._audioContext) {
          this._audioContext.close().catch(() => {});
          this._audioContext = null;
        }
      };

      // Start recording with timeslice to ensure periodic dataavailable
      this._mediaRecorder.start(1000);
      this._isRecording = true;
      this._startTime = Date.now();

      // UI updates
      this.updateStateUI("recording");

      this.timerTarget.style.display = "inline-block";
      // Timer
      this._timerId = setInterval(() => this.updateTimer(), 100);

      // Visualization loop
      this.visualize();
    } catch (error) {
      console.warn("[public--audio-interview-question] Microphone access denied or error:", error);
      this.showPermission("Microphone access denied. Please enable microphone permissions and try again.");
    }
  }

  stop() {
    if (this._mediaRecorder && this._mediaRecorder.state === "recording") {
      // Before stopping, snapshot an approximate duration from the timer
      try {
        const stopTs = Date.now();
        const startTs = this._startTime || stopTs;
        this._lastDurationSeconds = Math.max(0, Math.floor((stopTs - startTs) / 1000));
      } catch (_) {}

      // Flush the final data slice if supported before stopping
      try { this._mediaRecorder.requestData && this._mediaRecorder.requestData(); } catch (_) {}
      this._mediaRecorder.stop();
    }
    this._isRecording = false;

    // UI updates
    this.updateStateUI("stopped");

    // Stop timer
    if (this._timerId) {
      clearInterval(this._timerId);
      this._timerId = null;
    }

    // Stop visualization
    if (this._animationId) {
      cancelAnimationFrame(this._animationId);
      this._animationId = null;
    }

    // Hide visualization surface
    try { this.visualizationRailTarget.style.display = "none"; } catch (_) {}
    this.visualizationTarget.style.display = "none";
  }

  cleanup() {
    try {
      if (this._mediaRecorder && this._mediaRecorder.state === "recording") {
        this._mediaRecorder.stop();
      }
    } catch (_) {}

    try {
      if (this._stream) this._stream.getTracks().forEach((t) => t.stop());
    } catch (_) {}

    this._mediaRecorder = null;
    this._stream = null;

    if (this._audioContext) {
      this._audioContext.close().catch(() => {});
      this._audioContext = null;
    }
    this._analyser = null;
    this._micSource = null;
    this._dataArray = null;

    if (this._timerId) {
      clearInterval(this._timerId);
      this._timerId = null;
    }
    if (this._animationId) {
      cancelAnimationFrame(this._animationId);
      this._animationId = null;
    }

    this._isRecording = false;
  }

  // UI helpers
  resetUI() {
    this.updateStateUI("idle");
    this.timerTarget.textContent = "00:00";
    this.timerTarget.style.display = "none";
    this.audioPlayerTarget.style.display = "none";
    this.retryButtonTarget.style.setProperty('display', 'none', 'important');
    this.nextButtonTarget.style.setProperty('display', 'none', 'important');
    try { this.visualizationRailTarget.style.display = "none"; } catch (_) {}
    this.visualizationTarget.style.display = "none";
  }

  updateStateUI(state) {
    // Button
    this.recordButtonTarget.className = "record-button shadow " + state;
    if (state === "recording") {
      this.recordIconTarget.textContent = "⏸️";
      this.statusTextTarget.textContent = "Recording... Click to stop";
      this.statusTextTarget.className = "status-text recording";
      try { this.visualizationRailTarget.style.display = "flex"; } catch (_) {}
      this.visualizationTarget.style.display = "flex";
    } else if (state === "stopped") {
      this.recordIconTarget.textContent = "✅";
      this.statusTextTarget.textContent = "Recording complete!";
      this.statusTextTarget.className = "status-text stopped";
    } else {
      // idle
      this.recordIconTarget.textContent = "🎤";
      this.statusTextTarget.textContent = "Click to start recording";
      this.statusTextTarget.className = "status-text idle";
    }
  }

  showPermission(message) {
    this.warningNoticeTarget.style.display = "block";
    const span = this.warningNoticeTarget.querySelector(".ui-warning-box__message");
    span.textContent = message;
  }

  hidePermission() {
    this.warningNoticeTarget.style.display = "none";
  }

  ensureBars() {
    // Create visualization bars if not already present
    if (this.visualizationTarget.children.length === 0) {
      for (let i = 0; i < 32; i++) {
        const bar = document.createElement("div");
        bar.className = "wave-bar";
        // Minimal inline styling; allow CSS to control widths/spacing/colors
        bar.style.height = "10px";
        this.visualizationTarget.appendChild(bar);
      }
    }
  }

  // Attempt to acquire a live microphone stream whose primary audio track is not muted.
  async acquireMicrophoneStream(preferredDeviceId = null) {
    const attempts = [
      preferredDeviceId ? { audio: { deviceId: { exact: preferredDeviceId }, channelCount: 1 } } : null,
      { audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } },
      { audio: { echoCancellation: false, noiseSuppression: false, channelCount: 1 } },
      { audio: true }
    ].filter(Boolean);

    for (const constraints of attempts) {
      try {
        const s = await navigator.mediaDevices.getUserMedia(constraints);
        const ok = await this.ensureTrackActive(s);
        if (ok) return s;
        this.stopStream(s);
      } catch (_) { /* continue */ }
    }

    // After permission, try explicit deviceIds (iterate until we find one that isn't muted)
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((d) => d.kind === "audioinput");
      // Put preferred device first if present
      const ordered = preferredDeviceId
        ? inputs.sort((a, b) => (a.deviceId === preferredDeviceId ? -1 : b.deviceId === preferredDeviceId ? 1 : 0))
        : inputs;
      for (const d of ordered) {
        try {
          const s = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: d.deviceId } } });
          const ok = await this.ensureTrackActive(s);
          if (ok) return s;
          this.stopStream(s);
        } catch (_) { /* try next device */ }
      }
    } catch (_) { /* ignore enumerate errors */ }

    throw new Error("Microphone track remained muted after multiple attempts.");
  }

  async ensureTrackActive(stream) {
    const tracks = (stream && stream.getAudioTracks && stream.getAudioTracks()) || [];
    const track = tracks[0];
    if (!track) return false;
    if (!track.muted) return true;

    // Some browsers briefly report muted then emit 'unmute' once audio flows
    return await new Promise((resolve) => {
      let settled = false;
      const onUnmute = () => {
        if (settled) return;
        settled = true;
        resolve(true);
      };
      try {
        track.addEventListener("unmute", onUnmute, { once: true });
      } catch (_) {}

      setTimeout(() => {
        if (settled) return;
        try { track.removeEventListener("unmute", onUnmute); } catch (_) {}
        resolve(!track.muted);
      }, 1500);
    });
  }

  stopStream(stream) {
    try { (stream.getTracks() || []).forEach((t) => t.stop()); } catch (_) {}
  }

  // UI: Toggle visibility of device dropdown and populate with audio inputs
  async toggleDeviceSelector(event) {
    if (event) event.preventDefault();
    try {
      // Ensure labels are available
      if (!(await this.populateDeviceList())) {
        // If labels are empty, request quick permission
        await navigator.mediaDevices.getUserMedia({ audio: true });
        await this.populateDeviceList();
      }
      const el = this.deviceContainerTarget;
      const visible = el.style.display !== "none";
      el.style.display = visible ? "none" : "block";
    } catch (e) {
      console.warn("[public--audio-interview-question] Unable to show device selector:", e);
    }
  }

  async populateDeviceList() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((d) => d.kind === "audioinput");
      if (!inputs.length) return false;

      // Clear and repopulate
      this.deviceSelectTarget.innerHTML = "";
      inputs.forEach((d) => {
        const opt = document.createElement("option");
        opt.value = d.deviceId;
        opt.textContent = d.label || "Microphone";
        this.deviceSelectTarget.appendChild(opt);
      });

      // Preselect saved device if present
      const saved = this.hasDeviceIdValue ? this.deviceIdValue : localStorage.getItem("aiMicDeviceId");
      if (saved) {
        const found = Array.from(this.deviceSelectTarget.options).find((o) => o.value === saved);
        if (found) this.deviceSelectTarget.value = saved;
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  async changeDevice() {
    try {
      const id = this.deviceSelectTarget.value;
      this.deviceIdValue = id;
      try { localStorage.setItem("aiMicDeviceId", id); } catch (_) {}

      // If currently recording, restart with the selected device
      const wasRecording = this._isRecording;
      if (wasRecording) {
        this.stop();
      }
      // Clean up current stream and reacquire for next start
      this.cleanup();
      // Start immediately for user confirmation
      await this.start();
    } catch (e) {
      console.warn("[public--audio-interview-question] Failed to switch mic:", e);
    }
  }

  updateTimer() {
    const elapsed = Date.now() - (this._startTime || Date.now());
    const totalSeconds = Math.floor(elapsed / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    this.timerTarget.textContent = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

    const limit = this.hasTimeLimitValue ? Number(this.timeLimitValue) : 300;
    if (totalSeconds >= limit) {
      this.stop();
    }
  }

  visualize() {
    if (!this._isRecording || !this._analyser || !this._dataArray) return;

    // Frequency-domain visualization
    this._analyser.getByteFrequencyData(this._dataArray);
    const bars = this.visualizationTarget.querySelectorAll(".wave-bar");
    bars.forEach((bar, index) => {
      const value = this._dataArray[index] || 0;
      const height = (value / 255) * 50 + 5; // 5-55px like before
      bar.style.height = `${height}px`;
      // Restore the previous dynamic hue; CSS can override if desired
      bar.style.background = `hsl(${200 + value / 5}, 70%, 50%)`;
    });

    // Silence detection using time-domain RMS
    const timeData = new Uint8Array(this._analyser.fftSize);
    this._analyser.getByteTimeDomainData(timeData);
    let sum = 0;
    for (let i = 0; i < timeData.length; i++) {
      const v = (timeData[i] - 128) / 128; // normalize -1..1
      sum += v * v;
    }
    const rms = Math.sqrt(sum / timeData.length);
    if (rms < 0.01) {
      // Start or continue silence window
      if (this._silenceStartTs == null) this._silenceStartTs = Date.now();
      const elapsed = Date.now() - this._silenceStartTs;
      if (elapsed >= this._silenceGraceMs && !this._warnedSilence) {
        console.warn("[public--audio-interview-question] Detected near-silence from microphone input (RMS ~", rms.toFixed(4), ").");
        this.warningNoticeTarget.style.display = "block";
        const span = this.warningNoticeTarget.querySelector(".ui-warning-box__message");
        const msg = "We are not detecting sound from your microphone. Check input selection and system input level, then try again.";
        span.textContent = msg;
        this._warnedSilence = true;
      }
    } else {
      // Sound detected: reset window and remove any warning
      this._silenceStartTs = null;
      this._warnedSilence = false;
      this.hidePermission();
    }

    this._animationId = requestAnimationFrame(() => this.visualize());
  }
}
